// Shared calculations and algorithms ported from the original SAMAGRA-1 dashboard spec

/**
 * Level 0 is "Pre-Primary" content, not a real grade — every place that displays a
 * difficulty level number to a user should go through this helper.
 */
export function levelLabel(lvl) {
  return Number(lvl) === 0 ? 'Pre-Primary' : `Grade ${lvl}`;
}

/**
 * A student's paper tests levels below their own registered grade, working up to
 * (but not including) their own grade.
 * - The pass bar per level is 80%.
 * - EXACTLY ONE tested level is allowed to be a "soft-fail" (60-79% correct) and still be forgiven in full,
 *   provided every OTHER tested level is a clean >=80% pass. In that case, mastery goes all the way
 *   to the ceiling (one above the highest level tested).
 * - If there's a genuine hard-fail (<60%) anywhere, or a second level also below 80%, no forgiveness applies:
 *   mastery falls back to the first (lowest, ascending) level that failed to clear 80% — that level's own
 *   number is reported directly as the mastery grade, even if a harder level was passed afterwards.
 */
export function calculateMasteryLevel(answers, levels) {
  if (!answers || !levels || answers.length !== levels.length) return 0;
  
  const stats = {};
  for (let i = 0; i < answers.length; i++) {
    const level = levels[i];
    const ans = answers[i];
    if (isNaN(level) || isNaN(ans)) continue;

    if (!stats[level]) stats[level] = { correct: 0, total: 0 };
    stats[level].total++;
    if (ans === 1) stats[level].correct++;
  }

  const testedLevels = Object.keys(stats).map(Number).sort((a, b) => a - b);
  if (testedLevels.length === 0) return 0;

  const ceiling = testedLevels[testedLevels.length - 1] + 1;

  // Count how many tested levels are soft-fails (60-79%) vs hard-fails (<60%)
  let softFailCount = 0;
  let hardFailCount = 0;
  
  for (const level of testedLevels) {
    const s = stats[level];
    const pct = s.total > 0 ? s.correct / s.total : 0;
    if (pct < 0.60) hardFailCount++;
    else if (pct < 0.80) softFailCount++;
  }

  // Clean pass, or exactly one forgivable soft-fail with everything else clean -> full ceiling credit
  if (hardFailCount === 0 && softFailCount <= 1) return ceiling;

  // Forgiveness doesn't qualify -> plain ascending first-fail-at-80% rule
  for (const level of testedLevels) {
    const s = stats[level];
    if (s.total > 0 && (s.correct / s.total) < 0.80) {
      return level;
    }
  }
  
  return ceiling; // fallback
}

/**
 * Computes detail metrics (correct count, total questions, score percentage) for each tested level.
 */
export function calculateLevelStats(answers, levels) {
  const stats = {};
  for (let i = 0; i < answers.length; i++) {
    const level = levels[i];
    const ans = answers[i];
    if (isNaN(level) || isNaN(ans)) continue;

    if (!stats[level]) stats[level] = { correct: 0, total: 0, percentage: 0 };
    stats[level].total++;
    if (ans === 1) stats[level].correct++;
  }
  
  Object.keys(stats).forEach(level => {
    if (stats[level].total > 0) {
      stats[level].percentage = (stats[level].correct / stats[level].total) * 100;
    }
  });
  
  return stats;
}

/**
 * Shared aggregation for any subset of students.
 * - Gap-based classification (target = min(7, registeredGrade), gap = target - mastery)
 * - Competent if BOTH subjects' gap <= 0
 * - Remediation if EITHER subject's gap >= 3 (3+ years below)
 * - Bridge covers everything in between (1-2 years below in either subject)
 */
export function computeGroupStats(students) {
  const participated = students.filter(s => s.participated);
  const n = participated.length;
  let comp = 0;
  let bridge = 0;
  let remed = 0;
  let engSum = 0;
  let mathSum = 0;
  let overallSum = 0;

  participated.forEach(s => {
    // Gap targets ceiling at 7
    // const target = Math.min(8, s.registeredGrade);
    const target =  s.registeredGrade;
    const engGap = target - s.englishMastery;
    const mathGap = target - s.mathMastery;
    
    if (engGap >= 3 || mathGap >= 3) {
      remed++;
    } else if (engGap <= 0 && mathGap <= 0) {
      comp++;
    } else {
      bridge++;
    }
    
    engSum += s.englishScore || 0;
    mathSum += s.mathScore || 0;
    overallSum += s.scorePercentage || 0;
  });

  return {
    total: students.length,
    participated: n,
    comp,
    bridge,
    remed,
    avgEnglish: n > 0 ? engSum / n : 0,
    avgMath: n > 0 ? mathSum / n : 0,
    avgOverall: n > 0 ? overallSum / n : 0,
  };
}
